import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useStore, useToast, useResource, useModal, usePage, useFieldArray } from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_SURV_INFO,
    SF_SURV_PRTCPNT_HEAD,
    SF_SURV_PRTCPNT_QUESTION_LIST,
    SF_SURV_USR_INFO,
} from "@/comn/comn/surv/services/SurvPrtcpntIntService";
import { usePageContext } from "@/comn/features/context/PageContext";

/*
 * Survey Participate
 * !== 설문 참여 ==!
 */

type SurvPrtcpntPpupProps = {
    qestnrId?: string; // 모달로 띄울 때 주입
    langCd?: "KO" | "EN" | "TZ";
    onAfterSubmit?: () => void; // 저장/제출 후 콜백(부모에서 모달 닫고 리스트 리프레시)
    onRequestClose?: () => void; // 모달 닫기 요청(선택)
};

type SurvItem = { quesItmId: string; itmCn: string; itmOrd?: string | number };
type SurvQuestion = {
    quesId: string;
    quesOrd?: string | number;
    quesCn: string;
    quesTpCd?: string; // "1": 객관식, "2": 주관식 ("OBJ"/"SUB")
    maxSeltCnt?: string | number | null;
    items?: SurvItem[];
};

export const SurvPrtcpntPpupInt = (props: SurvPrtcpntPpupProps): JSX.Element => {
    const { ref } = usePageContext();

    if (ref?.current) {
        ref.current.isPopup = true;
    }

    const { pgeUid, menuId } = usePage({ pgeUid: "ptli0305002q" });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();
    const modal = useModal();
    const { pgeStore, setStore } = useStore({ pgeUid });
    const routeParams = useParams();

    const hasSurvPpupClose = useRef(false);
    const reloadOnSuccess = useRef(false);

    const [quesList, setQuesList] = useState<SurvQuestion[]>([]);

    // URL 파람 또는 props 우선
    const qestnrId = props.qestnrId ?? routeParams.qestnrId;

    const hasSessionUser = () => !!sessionStorage.getItem("user-info");

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" }, // 업무구분
            { area: "comnCd", comnCd: "COM0052" }, // 포털유형
        ],
    });

    // 폼 정의
    const form = {
        survInfo: useForm({
            defaultSchema: SF_SURV_INFO,
            defaultValues: {},
        }),

        survHead: useForm({
            defaultSchema: SF_SURV_PRTCPNT_HEAD,
            defaultValues: {},
        }),
        survBody: useForm({
            defaultSchema: SF_SURV_PRTCPNT_QUESTION_LIST,
            defaultValues: {},
        }),
        survUsrInfo: useForm({
            defaultSchema: SF_SURV_USR_INFO,
            defaultValues: {},
        }),
    };

    const { fields: quesFields, append, remove } = useFieldArray(form.survBody.schema.questions);

    const readSessionUser = () => {
        try {
            const raw = sessionStorage.getItem("user-info") || localStorage.getItem("user-info");
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    };

    const readSessionLangCd = (): "KO" | "EN" | "TZ" => {
        try {
            const raw = sessionStorage.getItem("menu-config") || localStorage.getItem("menu-config");
            const obj = raw ? JSON.parse(raw) : {};
            const lang = String(obj?.lang || "en")
                .slice(0, 2)
                .toUpperCase(); // "en" -> "EN"
            return (["KO", "EN", "TZ"] as const).includes(lang as any) ? (lang as any) : "EN";
        } catch {
            return "EN";
        }
    };

    const langCd = readSessionLangCd();

    // Fetch들
    const fetch = {
        getSurvDtl: useFetch({
            api: () => APIS.getSurvDtl({ qestnrId: String(qestnrId || ""), langCd }),
            enabled: !!qestnrId,
            onSuccess: (data) => {
                const content = data?.survDtl?.content ?? data?.survDtl;
                if (!content) return;

                // 헤더 세팅
                form.survHead.setValues({
                    qustnrTtle: content.qustnrTtle,
                    qustnrPupsCn: content.qustnrPupsCn,
                    qustnrStrtDt: content.qustnrStrtDt,
                    qustnrEndDt: content.qustnrEndDt,
                });
                form.survHead.setEditable("qustnrTtle", false);
                form.survHead.setEditable("qustnrPupsCn", false);
                form.survHead.setEditable("reqDateRnge", false);

                // 질문/항목 정렬
                const toNum = (v: any, def = 999999) => {
                    const n = Number(String(v ?? "").trim());
                    return Number.isFinite(n) ? n : def;
                };
                const sortedQs = (content.questions ?? [])
                    .slice()
                    .sort((a: any, b: any) => toNum(a.quesOrd) - toNum(b.quesOrd))
                    .map((q: any) => ({
                        ...q,
                        items: (q.items ?? []).slice().sort((a: any, b: any) => toNum(a.itmOrd) - toNum(b.itmOrd)),
                    }));

                setQuesList(sortedQs);
                form.survBody.setValue("questions", []); // 내부 상태 초기화
                sortedQs.forEach((q: any) => {
                    append({
                        quesCn: q.quesCn ?? "",
                        quesTpCd: q.quesTpCd ?? "",
                        itmCn: "", // 객관식 선택값
                        itmCns: [],
                        itemText: "", // 주관식 입력값
                    });
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00026" }),
            showToast: true,
        }),

        saveSurvRspn: useFetch({
            enabled: false,
            api: (data: any) => APIS.saveSurvRspn(data),
            onSuccess: () => {
                toast.showToast({ type: "success", content: "msg.com.00062" }); // 저장되었습니다.

                if (reloadOnSuccess.current) {
                    const url = window.location.pathname + window.location.search + window.location.search;
                    window.location.replace(url);
                    return;
                }

                if (props.onAfterSubmit) {
                    props.onAfterSubmit();
                } else {
                    navigate(URLS.ptli0305001q);
                }
            },
            onError: (error) => {
                if (error?.response?.status === 400 && error?.response?.data?.errors) {
                    form.survBody.setErrors(error.response.data.errors);
                }
                toast.showToast({
                    type: "warning",
                    content: t("msg.ptl.00682", { 0: t("T_QUESTIONS") }),
                });
            },
        }),
    };

    const saveNextTimeOnClose = async () => {
        if (hasSurvPpupClose.current) return; // 버튼 저장/제출 이후면 스킵
        if (!qestnrId) return;

        const sessionUser: any = readSessionUser?.() ?? {};
        form.survUsrInfo.setValue("ptlTpCd", "I");
        if (!form.survUsrInfo.watch("usrNm")) {
            form.survUsrInfo.setValue("usrNm", sessionUser?.usrNm);
        }
        if (!form.survUsrInfo.watch("usrDmnId")) {
            form.survUsrInfo.setValue("usrDmnId", sessionUser?.usrDmnId ?? sessionUser?.asisSysmUsrId);
        }
        if (!form.survUsrInfo.watch("cstmSctnOrgzCd")) {
            form.survUsrInfo.setValue("cstmSctnOrgzCd", sessionUser?.cstmSctnOrgzCd ?? "");
        }
        if (!form.survUsrInfo.watch("cstmUnitOrgzCd")) {
            form.survUsrInfo.setValue("cstmUnitOrgzCd", sessionUser?.cstmUnitOrgzCd ?? "");
        }
        if (!form.survUsrInfo.watch("roleNm")) {
            form.survUsrInfo.setValue("roleNm", sessionUser?.roleNm ?? "");
        }
        const survUsrInfo = form.survUsrInfo.getValues();

        const payload = {
            qestnrId: String(qestnrId),
            answers: [], // 닫을 때는 본문 없이 헤더만
            survUsrInfo,
            sbmtYn: "N",
        };

        try {
            await APIS.saveSurvRspn(payload);
        } catch {}
    };

    // 이벤트 핸들러
    const handler = {
        onSubmit: async (sbmtYn: "Y" | "N") => {
            // 다음에 하기 버튼 ( handleSubmit X )
            if (sbmtYn === "N") {
                modal.openModal({
                    content: "msg.ptl.00834", // Would you like to do this next time? !== 다음에 하시겠습니까? ==!
                    onConfirm: async () => {
                        hasSurvPpupClose.current = true; // 닫힘 자동처리 방지

                        // 유저 정보 최소 세팅 (검증 없이 값만 채워서 보냄)
                        const sessionUser: any = readSessionUser?.() ?? {};
                        form.survUsrInfo.setValue("ptlTpCd", "I");
                        if (!form.survUsrInfo.watch("usrNm")) {
                            form.survUsrInfo.setValue("usrNm", sessionUser?.usrNm);
                        }
                        if (!form.survUsrInfo.watch("usrDmnId")) {
                            form.survUsrInfo.setValue("usrDmnId", sessionUser?.usrDmnId ?? sessionUser?.asisSysmUsrId);
                        }
                        const survUsrInfo = form.survUsrInfo.getValues();

                        const payload = {
                            qestnrId: String(qestnrId),
                            answers: [],
                            survUsrInfo,
                            sbmtYn: "N",
                        };
                        reloadOnSuccess.current = true;
                        await fetch.saveSurvRspn.fetch(payload);
                    },
                });
                return;
            } else {
                // 제출 하기 버튼 ( handleSubmit O )
                modal.openModal({
                    content: "msg.00102", // Do you want to submit ?
                    onConfirm: () => {
                        form.survUsrInfo.handleSubmit(
                            async () => {
                                const body = form.survBody.getValues() as any;
                                const arr = body?.questions ?? [];

                                let invalidIndex: number | null = null;
                                for (let i = 0; i < quesList.length; i++) {
                                    const question = quesList[i];
                                    const row = arr[i];
                                    const quesTpCd = String(question?.quesTpCd ?? "")
                                        .trim()
                                        .toUpperCase();
                                    const isObjective = quesTpCd === "1" || quesTpCd === "OBJ";
                                    const isSubjective =
                                        quesTpCd === "2" || quesTpCd === "SUB" || (question.items ?? []).length === 0;
                                    const maxSelectCount = Math.max(1, Number(question?.maxSeltCnt ?? 1));

                                    if (isObjective) {
                                        if (maxSelectCount > 1) {
                                            const picked: string[] = row?.itmCns ?? [];
                                            if (!picked.length || picked.length > maxSelectCount) {
                                                invalidIndex = i;
                                                break;
                                            }
                                        } else {
                                            if (!row?.itmCn) {
                                                invalidIndex = i;
                                                break;
                                            }
                                        }
                                    } else if (isSubjective) {
                                        if (!row?.itemText || !String(row.itemText).trim()) {
                                            invalidIndex = i;
                                            break;
                                        }
                                    }
                                }

                                if (invalidIndex !== null) {
                                    const question = quesList[invalidIndex];
                                    const maxSelectCount = Math.max(1, Number(question?.maxSeltCnt ?? 1));
                                    const field =
                                        String(question?.quesTpCd ?? "").toUpperCase() === "1" && maxSelectCount > 1
                                            ? `questions.${invalidIndex}.itmCns`
                                            : String(question?.quesTpCd ?? "").toUpperCase() === "1"
                                              ? `questions.${invalidIndex}.itmCn`
                                              : `questions.${invalidIndex}.itemText`;
                                    form.survBody.setError(field, { type: "error", message: "msg.com.00005" });
                                    toast.showToast({
                                        type: "warning",
                                        content: t("msg.ptl.00682", { 0: t("T_QUESTIONS") }),
                                    });
                                    return;
                                }

                                const values = form.survBody.getValues() as any;
                                const questions = values?.questions ?? [];

                                const answers = questions
                                    .map((row: any, i: number) => {
                                        const question = quesList[i];
                                        const quesTpCd = String(question?.quesTpCd ?? "").toUpperCase();
                                        const maxSelectCount = Math.max(1, Number(question?.maxSeltCnt ?? 1));

                                        if ((quesTpCd === "1" || quesTpCd === "OBJ") && maxSelectCount <= 1) {
                                            return {
                                                quesId: question.quesId,
                                                quesItmId: row.itmCn ? [row.itmCn] : [],
                                                textAnswer: null,
                                            };
                                        }
                                        if ((quesTpCd === "1" || quesTpCd === "OBJ") && maxSelectCount > 1) {
                                            const ids: string[] = Array.isArray(row.itmCns) ? row.itmCns : [];
                                            return { quesId: question.quesId, quesItmId: ids, textAnswer: null };
                                        }
                                        const text = (row.itemText ?? "").trim();
                                        return { quesId: question.quesId, quesItmId: [], textAnswer: text || null };
                                    })
                                    .filter((row: any) => row.quesItmId?.length || row.textAnswer);

                                if (!qestnrId) {
                                    toast.showToast({ type: "error", content: "msg.com.00025" });
                                    return;
                                }

                                const sessionUser: any = readSessionUser?.() ?? {};
                                form.survUsrInfo.setValue("ptlTpCd", "I");
                                if (!form.survUsrInfo.watch("usrNm"))
                                    form.survUsrInfo.setValue("usrNm", sessionUser?.usrNm);
                                if (!form.survUsrInfo.watch("usrDmnId"))
                                    form.survUsrInfo.setValue(
                                        "usrDmnId",
                                        sessionUser?.usrDmnId ?? sessionUser?.asisSysmUsrId,
                                    );
                                const survUsrInfo = form.survUsrInfo.getValues();

                                const payload = { qestnrId: String(qestnrId), answers, survUsrInfo, sbmtYn: "Y" };

                                hasSurvPpupClose.current = true; // 닫힘 자동처리 방지
                                reloadOnSuccess.current = true;
                                await fetch.saveSurvRspn.fetch(payload);
                            },
                            () => {
                                if (!comnUtils.isEmpty(form.survUsrInfo.errors)) {
                                    toast.showToast({
                                        type: "warning",
                                        content: t("msg.ptl.00682", { 0: t("T_SURVEY_USERINFO") }),
                                    });
                                }
                            },
                        )();
                    },
                });
            }
        },
        onBtnCloseClick: () => {},
    };

    // 초기화
    useEffect(() => {
        const usr: any = readSessionUser();
        form.survUsrInfo.setValues({
            //내부 사용자
            usrNm: usr?.usrNm ?? "",
            usrDmnId: usr?.usrId ?? usr?.asisSysmUsrId ?? "",
            cstmSctnOrgzCd: usr?.sctnCd ?? "",
            cstmUnitOrgzCd: usr?.unitCd ?? "",
            usrTin: usr?.tin ?? "",
            roleNm: usr?.roleId ?? "",
            cstmOfcrNo: usr?.cstmOfcrNo,
        });
        const isLoggedIn = hasSessionUser();
        form.survUsrInfo.setEditable("usrNm", !isLoggedIn);
        form.survUsrInfo.setEditable("usrDmnId", !isLoggedIn);
        fetch.getSurvDtl.fetch();
    }, []);

    useEffect(() => {
        // 페이지/모달이 닫히거나 다른 화면으로 이동해서 이 컴포넌트가 unmount 될 때 실행
        return () => {
            if (!hasSurvPpupClose.current) {
                modal.openModal({
                    content: "msg.ptl.00834", // Would you like to do this next time? !== 다음에 하시겠습니까? ==!
                    onConfirm: () => {
                        saveNextTimeOnClose();
                    },
                });
            }
        };
    }, []);

    return (
        <Page pgeUid={pgeUid} info={true}>
            <Group>
                <Group.Header>
                    <Group.Title title={t("T_SURVEY_INFO") || "Survey"} />
                </Group.Header>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survHead.schema.qustnrTtle} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survHead.schema.qustnrPupsCn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survHead.schema.reqDateRnge} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Header>
                    <Group.Title title={t("T_SURVEY_USERINFO") || "User Info"} />
                </Group.Header>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survUsrInfo.schema.usrDmnId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survUsrInfo.schema.cstmSctnOrgzCd}
                                popupParams={{ cstmOrgzTpCd: "S" }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survUsrInfo.schema.cstmUnitOrgzCd}
                                popupParams={{ cstmOrgzTpCd: "U" }}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survUsrInfo.schema.roleNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survUsrInfo.schema.sysmUseFreqCd} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Header>
                    <Group.Title title={t("T_QUESTIONS")} />
                </Group.Header>
                <Group.Body>
                    {quesFields.map((survPrtcpntList, i) => {
                        const qList = quesList[i];
                        if (!qList) return null;

                        const tp = String(qList?.quesTpCd ?? "")
                            .trim()
                            .toUpperCase();
                        const Obj = tp === "1" || tp === "OBJ";
                        const Sub = tp === "2" || tp === "SUB" || (qList.items ?? []).length === 0;
                        const max = Math.max(1, Number(qList?.maxSeltCnt ?? 1)); // 기본 1

                        survPrtcpntList.schema.quesCn.readOnly = true;

                        // 보기 옵션(단일선택용)
                        const options = (qList.items ?? []).map((itm) => ({
                            label: itm.itmCn,
                            value: String(itm.quesItmId),
                        }));

                        // 단일선택(=기존)
                        survPrtcpntList.schema.itmCn.options = options;
                        survPrtcpntList.schema.itmCn.hidden = !(Obj && max <= 1);

                        // 다중선택(=checkbox)
                        survPrtcpntList.schema.itmCns.options = options;
                        survPrtcpntList.schema.itmCns.hidden = !(Obj && max > 1);

                        const picked: string[] = form.survBody.watch(`questions.${i}.itmCns`) ?? [];

                        survPrtcpntList.schema.itmCns.onChange = (next: string[]) => {
                            if (next.length > max) {
                                toast.showToast({ type: "warning", content: "msg.ptl.00839" }); // The maximum selection count for items has been exceeded., !== 항목 최대 선택 개수를 초과하였습니다. ==!

                                form.survBody.setValue(`questions.${i}.itmCns`, picked, { shouldValidate: false });
                                return;
                            }
                            form.survBody.setValue(`questions.${i}.itmCns`, next, { shouldValidate: false });
                        };

                        return (
                            <Group.Section key={survPrtcpntList.key}>
                                <Group.Row>
                                    <Group.Control {...survPrtcpntList.schema.quesCn} readOnly={true} />
                                </Group.Row>

                                {/* 단일선택 */}
                                {Obj && max <= 1 && (
                                    <Group.Row>
                                        <Group.Control {...survPrtcpntList.schema.itmCn} />
                                    </Group.Row>
                                )}

                                {/* 다중선택(체크박스 그룹) */}
                                {Obj && max > 1 && (
                                    <Group.Row>
                                        <Group.Control {...survPrtcpntList.schema.itmCns} />
                                    </Group.Row>
                                )}

                                {/* 주관식 */}
                                {Sub && (
                                    <Group.Row>
                                        <Group.Control {...survPrtcpntList.schema.itemText} />
                                    </Group.Row>
                                )}
                            </Group.Section>
                        );
                    })}
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Right>
                    <Button onClick={() => handler.onSubmit("N")}>{t("B_STRT_NEXT_TIMES")}</Button> {/* 다음에 하기 */}
                    <Button role="submit" onClick={() => handler.onSubmit("Y")} /> {/* 설문 제출 */}
                </Layout.Right>
            </Layout>
        </Page>
    );
};
